import { useEffect } from 'react';

interface ServiceModalProps {
  service: {
    id: number;
    title: string;
    description: string;
    icon: string;
    image: string;
  };
  isOpen: boolean;
  onClose: () => void;
}

const ServiceModal = ({ service, isOpen, onClose }: ServiceModalProps) => {
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }

    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [isOpen]);

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="flex items-center justify-center min-h-screen px-4 pt-4 pb-20 text-center sm:block sm:p-0">
        <div 
          className="fixed inset-0 transition-opacity bg-gray-500 bg-opacity-75"
          onClick={onClose}
        />

        <div className="inline-block w-full max-w-4xl my-8 overflow-hidden text-left align-middle transition-all transform bg-white shadow-xl rounded-2xl">
          <div className="relative">
            <button
              onClick={onClose}
              className="absolute top-4 right-4 z-10 w-10 h-10 bg-white/90 rounded-full flex items-center justify-center hover:bg-white transition-colors"
            >
              <i className="ri-close-line text-xl text-gray-600"></i>
            </button>

            <div className="relative h-64 md:h-80">
              <img
                src={service.image}
                alt={service.title}
                className="w-full h-full object-cover object-top"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent" />
              <div className="absolute bottom-6 left-6">
                <div className="w-16 h-16 bg-white/90 rounded-xl flex items-center justify-center mb-4">
                  <i className={`${service.icon} text-3xl text-blue-600`}></i>
                </div>
                <h2 className="text-3xl font-bold text-white">{service.title}</h2>
              </div>
            </div>

            <div className="p-8">
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
                <div>
                  <h3 className="text-xl font-semibold text-gray-900 mb-4">Service Description</h3>
                  <p className="text-gray-600 mb-6 leading-relaxed">
                    {service.description}
                  </p>

                  <h4 className="text-lg font-semibold text-gray-900 mb-3">What's Included:</h4>
                  <ul className="space-y-2 text-gray-600">
                    <li className="flex items-center">
                      <i className="ri-check-line text-green-600 mr-2"></i>
                      Professional consultation and assessment
                    </li>
                    <li className="flex items-center">
                      <i className="ri-check-line text-green-600 mr-2"></i>
                      High-quality materials and tools
                    </li>
                    <li className="flex items-center">
                      <i className="ri-check-line text-green-600 mr-2"></i>
                      Experienced craftsmen
                    </li>
                    <li className="flex items-center">
                      <i className="ri-check-line text-green-600 mr-2"></i>
                      Quality guarantee
                    </li>
                    <li className="flex items-center">
                      <i className="ri-check-line text-green-600 mr-2"></i>
                      Clean-up after completion
                    </li>
                  </ul>
                </div>

                <div>
                  <h3 className="text-xl font-semibold text-gray-900 mb-4">Get a Quote</h3>
                  <div className="bg-gray-50 rounded-xl p-6">
                    <p className="text-gray-600 mb-4">
                      Interested in this service? Contact us for a free consultation and personalized quote.
                    </p>
                    
                    <div className="space-y-3 mb-6">
                      <div className="flex items-center text-gray-700">
                        <i className="ri-phone-line text-blue-600 mr-3"></i>
                        <span>+41 77 218 45 02</span>
                      </div>
                      <div className="flex items-center text-gray-700">
                        <i className="ri-mail-line text-blue-600 mr-3"></i>
                        <span>info@alpbuild.ch</span>
                      </div>
                      <div className="flex items-center text-gray-700">
                        <i className="ri-whatsapp-line text-blue-600 mr-3"></i>
                        <span>WhatsApp: +41 77 218 45 02</span>
                      </div>
                    </div>

                    <div className="flex flex-col sm:flex-row gap-3">
                      <a
                        href="tel:+41772184502"
                        className="flex-1 bg-blue-600 text-white px-4 py-3 rounded-lg font-medium text-center hover:bg-blue-700 transition-colors whitespace-nowrap"
                      >
                        <i className="ri-phone-line mr-2"></i>
                        Call Now
                      </a>
                      <a
                        href="https://wa.me/41772184502"
                        target="_blank"
                        rel="noopener noreferrer"
                        className="flex-1 bg-green-600 text-white px-4 py-3 rounded-lg font-medium text-center hover:bg-green-700 transition-colors whitespace-nowrap"
                      >
                        <i className="ri-whatsapp-line mr-2"></i>
                        WhatsApp
                      </a>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ServiceModal;